import React from 'react';
import './SpiritualIntegration.css';

const SpiritualIntegration = () => {
  const attachmentStyles = [
    {
      type: "Secure Attachment",
      icon: "✅",
      description: "Sees God as loving, reliable, and accepting",
      characteristics: [
        "Experiences a consistent relationship with God",
        "Feels safe turning to God in times of need or suffering",
        "Engages in a trusting, emotionally intimate spiritual life"
      ]
    },
    {
      type: "Anxious (Preoccupied) Attachment",
      icon: "🔁",
      description: "Sees God as loving but unpredictable",
      characteristics: [
        "May fear abandonment or feel they're 'not doing enough' to earn God's love",
        "Engages in intense but sometimes fear-driven spirituality",
        "May experience guilt, spiritual striving, or worry about being 'good enough'"
      ]
    },
    {
      type: "Avoidant (Dismissive) Attachment",
      icon: "🚪",
      description: "Sees God as distant or irrelevant",
      characteristics: [
        "May resist vulnerability in prayer or spiritual practices",
        "Feels self-sufficient and avoids depending on God",
        "Religion or spirituality may feel intellectualized or superficial"
      ]
    },
    {
      type: "Disorganized (Fearful) Attachment",
      icon: "❓",
      description: "Sees God as both desirable and threatening",
      characteristics: [
        "May have a conflicted relationship with God (e.g., feeling punished or abandoned by God while also longing for connection)",
        "Spirituality might evoke shame, confusion, or fear",
        "Often linked to a history of trauma or spiritual abuse"
      ]
    }
  ];

  const therapeuticInsights = [
    "Their core beliefs about themselves and the world",
    "How they cope with distress (do they seek divine comfort or retreat in fear?)",
    "The emotional tone of their spiritual life (secure, anxious, avoidant, or ambivalent)"
  ];

  const healingAreas = [
    "Explore and reframe their image of God",
    "Heal from religious trauma or fear-based beliefs",
    "Develop a more secure, nurturing spiritual connection"
  ];

  return (
    <div className="spiritual-integration">
      <div className="spiritual-integration-container">
        <div className="spiritual-integration-header">
          <h1>Spiritual Integration</h1>
          <p>
            Understanding the connection between attachment patterns and spiritual relationships
          </p>
        </div>
        
        <div className="spiritual-integration-content">
          <div className="spiritual-intro">
            <p>
              Attachment theory can provide a powerful lens for understanding how people relate to God, 
              the divine, or spiritual figures, especially in the context of emotional security, trust, 
              and connection. Just as humans form attachment styles based on early caregiving relationships, 
              they can also form spiritual attachments that mirror those patterns—sometimes consciously, 
              often unconsciously.
            </p>
            <p>
              People tend to relate to God in ways that mirror their early attachment styles. Here's how 
              each style might look in a spiritual context:
            </p>
          </div>

          <div className="attachment-styles">
            {attachmentStyles.map((style, index) => (
              <div key={index} className="attachment-style-card">
                <div className="style-header">
                  <span className="style-icon">{style.icon}</span>
                  <h3>{style.type}</h3>
                </div>
                <p className="style-description">{style.description}</p>
                <ul className="style-characteristics">
                  {style.characteristics.map((characteristic, charIndex) => (
                    <li key={charIndex}>{characteristic}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="therapeutic-insights">
            <h2>For Therapists Working from an Attachment-Informed Lens</h2>
            <p>
              Understanding a client's spiritual attachment can offer important insights into:
            </p>
            <ul className="insights-list">
              {therapeuticInsights.map((insight, index) => (
                <li key={index}>{insight}</li>
              ))}
            </ul>
          </div>

          <div className="healing-process">
            <h2>When Clients Experience Spiritual Wounding or Confusion</h2>
            <p>
              Therapy can help them:
            </p>
            <ul className="healing-list">
              {healingAreas.map((area, index) => (
                <li key={index}>{area}</li>
              ))}
            </ul>
          </div>

          <div className="spiritual-credential">
            <h2>My Training & Expertise</h2>
            <div className="credential-card">
              <div className="credential-icon">🎓</div>
              <div className="credential-content">
                <h3>Master's Degree in Mental Health Counseling</h3>
                <p className="institution">Seminary of the Southwest</p>
                <p className="focus">Focus: Spiritual Integration in Mental Health Counseling</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualIntegration;
